/**
 * api-fetch/cards — verbatim KanbanApi method block extracted from the
 * former api.fetch.ts monolith. Closes over the shared {@link ApiFetchContext};
 * behaviour is unchanged. Composed by spread in createKanbanFetchApi.
 */
import type {
  ActionExecution,
  ActionTemplate,
  Allocation,
  Board,
  BoardTemplate,
  BoardTemplateAuditLogPage,
  BoardTemplateIntegrityReport,
  CardAiThread,
  CardChecklistItemT,
  CardChecklistT,
  CardCommentT,
  CardDependency,
  CostIntelligenceSummary,
  QuoteStageSummaryRow,
  QuoteConvertOptions,
  QuoteConvertResult,
  TrelloCardDetail,
  CardScheduleContext,
  ClassificationRule,
  EnsureTemplateLaneIdsResult,
  Lane,
  BoardMembership,
  LaneMembership,
  LinkBoardToTemplateInput,
  LinkBoardToTemplateResult,
  ListAuditLogOptions,
  ListBoardTemplatesOptions,
  ListWorkflowPresetsOptions,
  BoardRelatedCounts,
  Notebook,
  NotebookActionItemT,
  NotebookListItem,
  NotebookRecordingT,
  OrgClassificationMemory,
  ReconcileBoardInput,
  ReconcileResult,
  RelatedCards,
  Tag,
  UnlinkBoardFromTemplateInput,
  WorkflowPreset,
} from '../types'

import type {
  ActionInvokeInput,
  ActionTemplateFilter,
  AllocateEmailToLaneInput,
  AllocateEmailToLaneResult,
  AllocationExplain,
  AllocationCreate,
  AllocationFilter,
  BoardCreate,
  BoardDefaultCategory,
  BoardListFilter,
  BoardSnapshot,
  BoardSummary,
  CardCreate,
  CardParseActionsResult,
  CardDependencyCreate,
  CardDependencyFilter,
  CardFilter,
  CardSearchResult,
  DueSoonFilter,
  LinkEmailToCardInput,
  LinkEmailToCardResult,
  KanbanCalendarActionExtract,
  AssignedToMeFilter,
  CardMoveInput,
  CardTeamMessage,
  CardTeamMessageListResponse,
  CardTeamsThread,
  KanbanAllocationConflict,
  KanbanApi,
  PlacementEffectV1,
  KanbanBoardVersion,
  KanbanCasOpts,
  KanbanSearchResponse,
  LaneCardCount,
  LaneCreate,
  LaneListOptions,
  InstallWorkflowPresetInput,
  InstallWorkflowPresetResult,
  PromoteLaneToBoardResult,
  SeedDefaultBoardsInput,
  SeedDefaultBoardsResult,
  TagCreate,
  TeamMemberAddInput,
  TeamMemberAddResult,
  TeamRosterSyncResult,
  TrelloCardWritebackPatch,
  TrelloCardWritebackResult,
  TrelloSyncStatusResponse,
  TrelloConnectionSettings,
  TrelloConnectionSettingsPatch,
  TrelloCardDriftList,
  TrelloCardDriftResolveResult,
  SyncDriftResolution,
} from '../api'
import { KanbanCardConflictError, KanbanNetworkError } from '../api'
import type { CardLanePlacement, TrelloEmailLinkDecisionResult } from '../types'
import type {
  BoardMeeting,
  MeetingAnalyzeResult,
  MeetingLivePayload,
  MeetingSegmentIngestResult,
  MeetingSuggestionDecision,
} from '../meetingTypes'
import { CardListSchema, CardSchema, validateOrWarn } from '../validation'
import { createCardAccessLocksApi } from './cardAccessLocks'
import {
  normalizeCardListWireForRenderer,
  normalizeCardWireForRenderer,
} from './cardWireBoundary'
import { createCardDocumentsApi } from './cardDocuments'
import { createCardLifecycleApi } from './cardLifecycle'
import type { ApiFetchContext } from './context'

export function createCardsApi(ctx: ApiFetchContext) {
  const { _request, _trelloRequest, _casHeaders } = ctx
  const readCard = (value: unknown, endpoint: string) =>
    normalizeCardWireForRenderer(value, endpoint)
  const readCardList = (value: unknown, endpoint: string) =>
    normalizeCardListWireForRenderer(value, endpoint)
  const requestCard = (endpoint: string, request: () => Promise<unknown>) =>
    request().then((value) => readCard(value, endpoint))
  const requestCardList = (endpoint: string, request: () => Promise<unknown>) =>
    request().then((value) => readCardList(value, endpoint))
  const readObject = (value: unknown, endpoint: string): Record<string, unknown> => {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      throw new Error(`Expected object response from ${endpoint}`)
    }
    return value as Record<string, unknown>
  }
  return {
    // Cards -----------------------------------------------------------
    listCards: async (filter: CardFilter) => {
      const value = await _request<unknown>('GET', '/cards/', undefined, {
        board: filter.boardId,
        lane: filter.laneId,
        status: filter.status,
        job: filter.jobId,
        sensitive:
          filter.sensitive === undefined ? undefined : filter.sensitive ? '1' : '0',
        // PERF-2026-05-25-KANBAN-LANE-LEAN — opts into qb!3088's lean
        // projection (drops summary / audit_log_json /
        // related_attachment_ids_json). Only emitted when the caller
        // explicitly asks; legacy callers (mobile, search, etc.) that
        // don't pass `lean` see the historical full payload.
        lean: filter.lean === true ? 'true' : undefined,
        // G2 — widen the lane fetch to INCLUDE archived cards (BE
        // `?include_archived=true`) so the board's "Archived" status
        // pill has cards to reveal. Only emitted when explicitly asked;
        // the default board load stays active-only.
        include_archived: filter.includeArchived === true ? 'true' : undefined,
      }, undefined, { signal: filter.signal })
      const cards = readCardList(value, 'GET /cards/')
      validateOrWarn(CardListSchema, cards, 'GET /cards/')
      return cards
    },
    listCardsDueSoon: async (filter?: DueSoonFilter) => {
      const value = await _request<unknown>('GET', '/cards/due-soon/', undefined, {
        horizon_days:
          filter?.horizonDays === undefined ? undefined : String(filter.horizonDays),
        include_done: filter?.includeDone === true ? '1' : undefined,
        organisation: filter?.organisationId,
      })
      const cards = readCardList(value, 'GET /cards/due-soon/')
      validateOrWarn(CardListSchema, cards, 'GET /cards/due-soon/')
      return cards
    },
    listCalendarActions: async (filter) => {
      return _request<KanbanCalendarActionExtract[]>(
        'GET',
        '/cards/calendar-actions/',
        undefined,
        {
          start: filter?.start,
          end: filter?.end,
          include_done: filter?.includeDone === true ? '1' : undefined,
          // Header X-Organisation-ID is the only org signal. A second
          // ?organisation= value 409s when the visible tenant disagrees
          // with session.organisation.id (Bravada 4 vs HQ 8).
        },
      )
    },
    assignedToMe: async (filter?: AssignedToMeFilter) => {
      const value = await _request<unknown>(
        'GET', '/cards/assigned-to-me/', undefined,
        {
          include_done: filter?.includeDone === true ? '1' : undefined,
          include_inbox: filter?.includeInbox === true ? '1' : undefined,
          organisation: filter?.organisationId,
        },
      )
      const cards = readCardList(value, 'GET /cards/assigned-to-me/')
      validateOrWarn(CardListSchema, cards, 'GET /cards/assigned-to-me/')
      return cards
    },
    getCard: async (id) => {
      const endpoint = `GET /cards/${id}/`
      const value = await _request<unknown>('GET', `/cards/${id}/`)
      const card = readCard(value, endpoint)
      validateOrWarn(CardSchema, card, endpoint)
      return card
    },
    createCard: (input: CardCreate) =>
      requestCard('POST /cards/', () => _request<unknown>('POST', '/cards/', input)),
    updateCard: (id, patch, opts) =>
      requestCard(`PATCH /cards/${id}/`, () =>
        _request<unknown>('PATCH', `/cards/${id}/`, patch, undefined, _casHeaders(opts)),
      ),
    moveCard: (id, input: CardMoveInput, opts) =>
      requestCard(`POST /cards/${id}/move/`, () =>
        _request<unknown>(
          'POST',
          `/cards/${id}/move/`,
          input,
          undefined,
          _casHeaders(opts),
        ),
      ),
    getBoardVersion: (boardId: string) =>
      _request<KanbanBoardVersion>('GET', `/boards/${boardId}/version/`),
    invokeAction: (cardId, input: ActionInvokeInput) =>
      _request<ActionExecution>('POST', `/cards/${cardId}/invoke-action/`, input),
    // EK-06 MR-C: hard delete. Backed by DRF's default ModelViewSet
    // destroy action on `email_kanban.views.cards.CardViewSet`.
    // See `KanbanApi.deleteCard` for the contract notes.
    deleteCard: async (id: string) => {
      await _request<void>('DELETE', `/cards/${id}/`)
    },

    // Stack 12 MR-C — card-tree write/read endpoints (qb!2966).
    // These wire the FE components' `api.setCardParent` /
    // `api.detachCardFromTree` / `api.fetchCardSubtree` /
    // `api.archiveCardSubtree` calls in `KanbanCard.onDropAsChild`,
    // `KanbanContextMenu`, and `CardDetailDrawer.SubCardsSection`.
    // Server pin tests: quotech-backend/email_kanban/tests/
    // test_stack12_mr_c_card_tree_pin.py (url_path verification).
    setCardParent: (cardId, body) =>
      requestCard(`POST /cards/${cardId}/set-parent/`, () =>
        _request<unknown>('POST', `/cards/${cardId}/set-parent/`, body),
      ),
    detachCardFromTree: (cardId) =>
      requestCard(`POST /cards/${cardId}/detach-from-tree/`, () =>
        _request<unknown>('POST', `/cards/${cardId}/detach-from-tree/`, {}),
      ),
    fetchCardSubtree: (cardId) =>
      requestCardList(`GET /cards/${cardId}/subtree/`, () =>
        _request<unknown>('GET', `/cards/${cardId}/subtree/`),
      ),
    archiveCardSubtree: (cardId) =>
      _request<{ archived: number; root_id: string }>(
        'POST',
        `/cards/${cardId}/archive-subtree/`,
        {},
      ),

    // ARC-10 linked cards — extra lane presences (qb!4363,
    // email_kanban/views/card_actions/placements.py). Same card row,
    // more lanes; the placement instance owns its own lane/position.
    // RESTORED 2026-07-07 — dropped by 83ca39fc4's merge resolution
    // (BUG-2026-07-07-ARC10-PLACEMENTS-API-STOMPED).
    executePlacementVerb: (cardId, intent) =>
      _request<PlacementEffectV1>(
        'POST',
        `/cards/${cardId}/placement-operation/`,
        intent,
      ),
    addCardPlacement: (cardId, body) =>
      _request<CardLanePlacement>('POST', `/cards/${cardId}/placements/`, body),
    updateCardPlacement: (cardId, placementId, body) =>
      _request<CardLanePlacement>(
        'PATCH',
        `/cards/${cardId}/placements/${placementId}/`,
        body,
      ),
    removeCardPlacement: async (cardId, placementId) => {
      await _request<void>(
        'DELETE',
        `/cards/${cardId}/placements/${placementId}/`,
      )
    },

    // ARC-3 (U17) — drawer Entries table: bulk-mint rows as dependency
    // sub-cards (be!4258, email_kanban/views/card_entries.py).
    createCardEntries: async (cardId, body) => {
      const endpoint = `POST /cards/${cardId}/entries/bulk/`
      const response = readObject(
        await _request<unknown>('POST', `/cards/${cardId}/entries/bulk/`, body),
        endpoint,
      )
      return {
        created: readCardList(response.created, `${endpoint}.created`),
        dependency_ids: Array.isArray(response.dependency_ids)
          ? response.dependency_ids.filter(
              (id): id is string => typeof id === 'string',
            )
          : [],
      }
    },

    // Newmark Stack 6 — bucket grouping endpoints (qb!3120). Backed
    // by `email_kanban.services.bucket_grouping` + the three actions
    // on `email_kanban.views.cards.CardViewSet`. All three return
    // stable `code` strings on 4xx so the FE can branch on the
    // reason — see `KanbanApi.groupCardsIntoBucket` for the list.
    groupCardsIntoBucket: async (cardIds, options) => {
      const endpoint = 'POST /cards/group-into-bucket/'
      const response = readObject(
        await _request<unknown>('POST', '/cards/group-into-bucket/', {
          card_ids: cardIds,
          title: options?.title ?? 'Bucket',
          summary: options?.summary ?? '',
        }),
        endpoint,
      )
      return {
        bucket: readCard(response.bucket, `${endpoint}.bucket`),
        children: readCardList(response.children, `${endpoint}.children`),
      }
    },
    addCardsToBucket: async (bucketId, cardIds) => {
      const endpoint = `POST /cards/${bucketId}/add-to-bucket/`
      const response = readObject(
        await _request<unknown>('POST', `/cards/${bucketId}/add-to-bucket/`, {
          card_ids: cardIds,
        }),
        endpoint,
      )
      return {
        children: readCardList(response.children, `${endpoint}.children`),
      }
    },
    ungroupBucket: async (bucketId, options) => {
      const endpoint = `POST /cards/${bucketId}/ungroup-bucket/`
      const response = readObject(
        await _request<unknown>('POST', `/cards/${bucketId}/ungroup-bucket/`, {
          delete_empty: options?.deleteEmpty ?? true,
        }),
        endpoint,
      )
      return {
        children: readCardList(response.children, `${endpoint}.children`),
        bucket_deleted: response.bucket_deleted === true,
      }
    },

    // Card dependencies (Cash Flow DAG) -------------------------------
    // FEAT-2026-05-18-KANBAN-CASHFLOW. Backed by
    // `email_kanban.views.cashflow.CardDependencyViewSet`.
    // See `KanbanApi.listCardDependencies` for the contract notes.
    //
    // The BE viewset returns a flat array (DefaultRouter, no DRF
    // pagination on this collection — edges are bounded by the
    // size of one board's card set, typically <500 edges). Server
    // returns `{ count, results }` only if pagination is wired
    // later — handle both shapes defensively so a future BE change
    // doesn't break the FE.
    listCardDependencies: async (filter: CardDependencyFilter) => {
      const value = await _request<CardDependency[] | { results: CardDependency[] }>(
        'GET',
        '/card-dependencies/',
        undefined,
        {
          board: filter.boardId,
          card: filter.cardId,
          from_card: filter.fromCardId,
          to_card: filter.toCardId,
        },
      )
      return Array.isArray(value) ? value : (value?.results ?? [])
    },
    createCardDependency: (input: CardDependencyCreate) =>
      _request<CardDependency>('POST', '/card-dependencies/', input),
    deleteCardDependency: async (id: string) => {
      await _request<void>('DELETE', `/card-dependencies/${id}/`)
    },

    // Cost intelligence (Dashboard view) ------------------------------
    // FEAT-2026-06-02-COST-INTELLIGENCE. Backed by the
    // `BoardViewSet.cost_intelligence` @action — aggregates the board
    // + its sub-boards by construction CoA category / group / supplier.
    getCostIntelligence: (boardId: string) =>
      _request<CostIntelligenceSummary>(
        'GET',
        `/boards/${boardId}/cost-intelligence/`,
      ),

    // Card tags -------------------------------------------------------
    attachTag: (cardId, tagId, source = 'USER') =>
      requestCard(`POST /cards/${cardId}/tags/`, () =>
        _request<unknown>('POST', `/cards/${cardId}/tags/`, { tag_id: tagId, source }),
      ),
    detachTag: (cardId, tagId) =>
      requestCard(`DELETE /cards/${cardId}/tags/${tagId}/`, () =>
        _request<unknown>('DELETE', `/cards/${cardId}/tags/${tagId}/`),
      ),

    // Card documents are intentionally separate so this primary card boundary
    // stays focused on renderer-safe Card responses.
    ...createCardDocumentsApi(ctx),

    // Card assignees --------------------------------------------------
    attachAssignee: (cardId, userId) =>
      requestCard(`POST /cards/${cardId}/assignees/`, () =>
        _request<unknown>('POST', `/cards/${cardId}/assignees/`, { user_id: userId }),
      ),
    detachAssignee: (cardId, userId) =>
      requestCard(`DELETE /cards/${cardId}/assignees/${userId}/`, () =>
        _request<unknown>('DELETE', `/cards/${cardId}/assignees/${userId}/`),
      ),

    // Card RBAC (3f) — backed by qb!2631 endpoint.
    restrictCardTo: (cardId, userIds) =>
      _request<{ id: string; restricted_to: number[] }>(
        'POST',
        `/cards/${cardId}/restrict/`,
        { user_ids: userIds },
      ),

    // Assignees (item 1 Phase 2) — backed by qb!2636.
    assignCard: (cardId, payload) =>
      requestCard(`POST /cards/${cardId}/assign/`, () =>
        _request<unknown>('POST', `/cards/${cardId}/assign/`, payload),
      ),

    // Soft-match suggestions (item 15 deep) — backed by qb!2639.
    getSimilarCards: (cardId, opts) => {
      const params = new URLSearchParams()
      if (opts?.k != null) params.set('k', String(opts.k))
      if (opts?.max_dist != null) params.set('max_dist', String(opts.max_dist))
      const qs = params.toString()
      return _request<{ card_id: string; model: string; results: Array<{ id: string; title: string; distance: number }> }>(
        'GET',
        `/cards/${cardId}/similar/${qs ? `?${qs}` : ''}`,
      )
    },

    // FEAT-2026-06-22-KANBAN-NOTEBOOKS Phase 3 — sub-chain recommender.
    getRelatedCards: (cardId, opts) =>
      _request<RelatedCards>('GET', `/cards/${cardId}/related/`, undefined, {
        limit: opts?.limit,
        max_dist: opts?.maxDist,
      }),

    // Pyramid scheduling (items 4/5) — backed by qb!2637.
    getCardScheduleContext: (cardId) =>
      _request<CardScheduleContext>('GET', `/cards/${cardId}/schedule-context/`),
    snapCardToJob: (cardId) =>
      requestCard(`POST /cards/${cardId}/snap-to-job/`, () =>
        _request<unknown>('POST', `/cards/${cardId}/snap-to-job/`, {}),
      ),

    // AI thread peek — backed by qb!3838 (FEAT-2026-06-20-KANBAN-CARD-AI-THREAD).
    getCardAiThread: (cardId, opts) => {
      const qs = opts?.refresh ? '?refresh=1' : ''
      return _request<CardAiThread>('GET', `/cards/${cardId}/ai-thread/${qs}`)
    },

    ...createCardAccessLocksApi(ctx),
    ...createCardLifecycleApi(ctx),

    // Card comments ---------------------------------------------------
    listComments: (cardId) =>
      _request<CardCommentT[]>('GET', `/cards/${cardId}/comments/`),
    // `comment_mentions` rides along only when someone is actually tagged —
    // an old backend that doesn't read it just ignores the extra key, so
    // this degrades to "mentions don't notify" rather than failing.
    createComment: (cardId, body, mentions) =>
      _request<CardCommentT>(
        'POST',
        `/cards/${cardId}/comments/`,
        mentions && mentions.length > 0
          ? { body, comment_mentions: mentions }
          : { body },
      ),
    updateComment: (commentId, body) =>
      _request<CardCommentT>('PATCH', `/comments/${commentId}/`, { body }),
    deleteComment: async (commentId) => {
      await _request<void>('DELETE', `/comments/${commentId}/`)
    },

    // `satisfies Partial<KanbanApi>` gives every arrow-method param its
    // contextual type from the KanbanApi contract (the monolith got these for
    // free as one `: KanbanApi` literal). Partial keeps the per-module subset
    // valid; the spread composition in api.fetch.ts still enforces completeness
    // against the full KanbanApi. Required under tenant strict (noImplicitAny).
  } satisfies Partial<KanbanApi>
}
